import { useCallback, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useEvent } from '@hooks/use-event';
import { throttle } from '@utils/browser';
import { fileNameFromPath } from '@utils/strings';
import { Timeout } from '@utils/types';

import { config } from 'app-config';

import { loadBlob } from './request';

const ELASTICSEARCH_REFRESH_RATE = 1000;
const MAX_RECONNECT_ATTEMPTS = 12;
const RECONNECT_DELAY = 5000;
const THROTTLE_TIME = 5000;

type WebsocketOptions = {
  enabled?: boolean;
  throttleTime?: number;
};

export function useWebSocket<T = unknown>(
  url: string,
  callback: (event: MessageEvent<T>) => void,
  options: WebsocketOptions = {}
) {
  const socketRef = useRef<WebSocket | null>(null);
  const stableCallback = useEvent(callback);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<Timeout | null>(null);

  const enabled = options?.enabled ?? true;
  const throttleTime = options?.throttleTime ?? THROTTLE_TIME;

  const connectWebSocket = useCallback(() => {
    if (!enabled) return;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    socketRef.current = new WebSocket(`${config.WS_URL}/${url}`);
    socketRef.current.onmessage = stableCallback;
    if (throttleTime > 0) {
      socketRef.current.onmessage = throttle(stableCallback, throttleTime, { trailing: true });
    }

    socketRef.current.onopen = () => {
      reconnectAttemptsRef.current = 0;
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    socketRef.current.onclose = () => {
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          connectWebSocket();
        }, RECONNECT_DELAY);
      }
    };
  }, [url, stableCallback, enabled, throttleTime]);

  useEffect(() => {
    reconnectAttemptsRef.current = 0;
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        // Clear handlers BEFORE closing to prevent orphaned reconnects.
        // socket.close() is async - onclose fires AFTER cleanup finishes,
        // which would schedule a new reconnect timeout that never gets cleared.
        socketRef.current.onmessage = null;
        socketRef.current.onopen = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.close();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [url, connectWebSocket]);
}

export const useFileFromUrl = (url: string) => {
  return useQuery({
    queryFn: async () => {
      const blob = await loadBlob(url);
      return new File([blob], fileNameFromPath(url));
    },
    queryKey: ['file-from-url', url],
    enabled: Boolean(url),
  });
};

export const waitForElasticRefresh = (callback: () => void | Promise<void>): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(async () => {
      await callback();
      resolve();
    }, ELASTICSEARCH_REFRESH_RATE);
  });
};
